import { addDays, format } from 'date-fns'
import type { MemoTemplate, MemoVariant } from '@/shared/templates/memos'

/**
 * Where a template's span opens: the day the memo belongs to, the lead, and then
 * forward to the weekday it says its span starts on, if it names one. A
 * vecko-PM is given out on a Monday for the week that starts there, so writing
 * one on the Tuesday is writing next Monday's.
 *
 * The lead is the template's own unless a column of it declares another - see
 * `columnLeadDays`.
 *
 * A template written in more than one shape then opens on the first day of one
 * of them - see `variantStart`.
 */
export function seedStart(
  memoDate: string,
  template: MemoTemplate | undefined,
  leadDays = template?.leadDays ?? 0
): string {
  const led = shift(memoDate, leadDays)
  const started = template?.spanStartWeekday === undefined
    ? led
    : forwardToWeekday(led, template.spanStartWeekday)

  return variantStart(started, template?.variants ?? [])
}

/**
 * The day a template with variants opens its span on: the day it was given,
 * where that falls on the first day of one of them, and otherwise forward to
 * the first variant's weekday.
 *
 * Which shape the memo is written in is read off that day, so a span opening on
 * neither would be written under a shape it does not cover the days of: a
 * helg-PM started on a Monday is the coming Friday through Sunday's, not
 * Tuesday through Thursday's under a Friday's title. Forward rather than to the
 * nearest, for the same reason the lead runs that way - a PM is written ahead of
 * the days it covers.
 */
function variantStart(day: string, variants: MemoVariant[]): string {
  if (!variants.length) {
    return day
  }

  const weekday = new Date(`${day}T00:00:00`).getDay()

  if (variants.some((variant) => variant.startWeekday === weekday)) {
    return day
  }

  return forwardToWeekday(day, variants[0].startWeekday)
}

/**
 * A `YYYY-MM-DD` day, that many days on. Parsed at midnight local time, the way
 * every other date in the dialog is, so a day never lands on the one before it.
 */
export function shift(day: string, days: number): string {
  return format(addDays(new Date(`${day}T00:00:00`), days), 'yyyy-MM-dd')
}

/**
 * A `YYYY-MM-DD` day moved forward to the next given weekday, `0` for Sunday
 * the way `Date#getDay` counts, and left where it is when it already falls on
 * one.
 */
function forwardToWeekday(day: string, weekday: number): string {
  const parsed = new Date(`${day}T00:00:00`)

  return format(addDays(parsed, (weekday - parsed.getDay() + 7) % 7), 'yyyy-MM-dd')
}
